import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import FilterDropdown from './FilterDropdown';
import SearchInput from './SearchInput';
import { fmtDateCompact } from '../lib/dates';
import { OttoSleeping } from './otto';

// Five-column Kanban board for /briefs. Maps the existing brief data
// model onto a lifecycle pipeline without changing the underlying
// API. Columns derive from a combination of brief.status,
// awarded_supplier_id, and response_count rather than any new column.
//
// Column derivation:
//   draft         status === 'draft'
//   sent          status === 'sent' && no responses yet
//   responses_in  status === 'sent' && at least one response
//   awarded       status === 'closed' && awarded_supplier_id set
//   closed        status === 'closed' (no award), or status === 'cancelled'
//
// Read-only view: status changes happen on the brief detail page,
// not by dragging cards across columns. Click a card to open the
// brief and drive it through the pipeline from there.

// `accent` is the locked top-border token per lane (build #14 Part B);
// `head` is the READABLE label colour. These diverge on purpose: Sent's
// identity is acid-green #92D400, but acid green fails WCAG as small text
// on the light lane header, so the label uses a readable green (#256B2E)
// while the top strip keeps the acid token. Closed's top border is the
// neutral #b9bdad token.
const COLUMNS = [
  { key: 'draft',        label: 'Draft',         accent: '#DADCCD', head: '#51534A', empty: 'No briefs in draft.' },
  { key: 'sent',         label: 'Sent',          accent: '#92D400', head: '#256B2E', empty: 'No briefs awaiting responses.' },
  { key: 'responses_in', label: 'Responses In',  accent: '#2E8FA1', head: '#2E8FA1', empty: 'No briefs have responses yet.' },
  { key: 'awarded',      label: 'Awarded',       accent: '#009159', head: '#009159', empty: 'No awarded briefs.' },
  { key: 'closed',       label: 'Closed',        accent: '#b9bdad', head: '#51534A', empty: 'Completed briefs will move here once their projects wrap.' },
];

// Card avatars are a single deep-green (build #10) — a considered
// "owner" marker, not a rainbow. Retires the old off-palette amber /
// purple entries; acid #92D400 stays reserved for actions.
const OWNER_PALETTE = [
  { bg: '#009159', text: '#F9FCF1' },
];
const hashStr = (s) => {
  let h = 0;
  if (!s) return 0;
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  return Math.abs(h);
};
const ownerColorFor = (id) => OWNER_PALETTE[hashStr(id) % OWNER_PALETTE.length];
const initialsFor = (name) => {
  if (!name) return '?';
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const columnFor = (b) => {
  if (b.status === 'draft') return 'draft';
  if (b.status === 'cancelled') return 'closed';
  if (b.status === 'closed') return b.awarded_supplier_id ? 'awarded' : 'closed';
  if (b.status === 'sent') return (b.response_count || 0) > 0 ? 'responses_in' : 'sent';
  return 'draft';
};

const urgencyState = (b) => {
  if (b.status !== 'sent' || !b.required_response_time) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const deadline = new Date(b.required_response_time);
  if (isNaN(deadline.getTime())) return null;
  const diffDays = (deadline.getTime() - today.getTime()) / 86400000;
  if (diffDays < 0) return 'overdue';
  if (diffDays <= 5) return 'due_soon';
  return null;
};

const formatDeadline = (b) => {
  if (!b.required_response_time) return { text: '—', tone: 'normal' };
  const urg = urgencyState(b);
  const d = new Date(b.required_response_time);
  if (urg === 'overdue') return { text: 'Overdue', tone: 'overdue' };
  if (urg === 'due_soon') {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const days = Math.max(0, Math.round((d.getTime() - today.getTime()) / 86400000));
    return { text: `Due in ${days}d`, tone: 'urgent' };
  }
  return {
    text: fmtDateCompact(d),
    tone: 'normal',
  };
};

export default function BriefsBoard({ briefs, currentUserId, commentCountsByBrief }) {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  // Owner filter: array of selected owner ids. Empty = no owner filter.
  // Default: [currentUserId] (Just Me).
  const [ownerFilter, setOwnerFilter] = useState(() => currentUserId ? [currentUserId] : []);
  const [categoryFilter, setCategoryFilter] = useState([]);

  const categories = useMemo(
    () => Array.from(new Set(briefs.map(b => (b.brief_category || '').trim()).filter(Boolean))).sort(),
    [briefs]
  );
  const ownersInData = useMemo(() => {
    const map = new Map();
    for (const b of briefs) {
      for (const o of (b.owners_list || [])) {
        if (o && o.id && !map.has(o.id)) map.set(o.id, o);
      }
    }
    return Array.from(map.values()).sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''));
  }, [briefs]);

  const justMeActive = ownerFilter.length === 1 && ownerFilter[0] === currentUserId;
  const toggleJustMe = () => {
    if (!currentUserId) return;
    setOwnerFilter(justMeActive ? [] : [currentUserId]);
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return briefs.filter(b => {
      if (b.archived_at) return false;
      if (ownerFilter.length > 0) {
        const owns = (b.owners_list || []).some(o => o && ownerFilter.includes(o.id));
        if (!owns) return false;
      }
      if (categoryFilter.length > 0 && !categoryFilter.includes(b.brief_category)) return false;
      if (q) {
        const hay = [
          b.brief_name, b.fusion_project_name, b.fusion_project_number, b.brief_category,
          ...(b.owners_list || []).map(o => o?.full_name),
        ].filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [briefs, ownerFilter, categoryFilter, search]);

  // Bucket the filtered briefs into the five columns, sorting each by
  // deadline ascending so the most urgent work surfaces at the top of
  // its column.
  const buckets = useMemo(() => {
    const out = { draft: [], sent: [], responses_in: [], awarded: [], closed: [] };
    for (const b of filtered) {
      const col = columnFor(b);
      if (out[col]) out[col].push(b);
    }
    for (const k of Object.keys(out)) {
      out[k].sort((a, b) => {
        const at = a.required_response_time ? new Date(a.required_response_time).getTime() : Infinity;
        const bt = b.required_response_time ? new Date(b.required_response_time).getTime() : Infinity;
        return at - bt;
      });
    }
    return out;
  }, [filtered]);

  return (
    <div className="flex-1 flex flex-col overflow-hidden px-8 pb-8 pt-2">
      {/* Toolbar — search + filter pills */}
      <div className="flex items-center gap-3 mb-5 flex-wrap flex-shrink-0">
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search briefs by name, project, category or owner..."
          className="flex-1 min-w-[280px]" />
        <button
          type="button"
          onClick={toggleJustMe}
          disabled={!currentUserId}
          className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl border font-['Metropolis'] text-[11px] font-bold tracking-[2px] uppercase whitespace-nowrap transition-colors disabled:opacity-40 ${
            justMeActive
              ? 'bg-[#92D400]/15 border-[#92D400] text-fusion-forest'
              : 'bg-white border-line text-[#51534A] hover:text-fusion-mid-grey'
          }`}>
          <span className="w-5 h-5 rounded-full bg-[#92D400] text-[#131313] inline-flex items-center justify-center text-[10px] font-bold tracking-normal">R</span>
          Just Me
        </button>
        <FilterDropdown
          label="Owners"
          options={ownersInData.map(o => ({ value: o.id, label: o.full_name }))}
          selected={ownerFilter}
          onChange={setOwnerFilter} />
        <FilterDropdown
          label="Categories"
          align="right"
          options={categories.map(c => ({ value: c, label: c }))}
          selected={categoryFilter}
          onChange={setCategoryFilter} />
      </div>

      {/* Board — horizontal scroll, columns scroll internally. */}
      <div className="flex-1 overflow-x-auto overflow-y-hidden min-h-0">
        <div className="grid grid-cols-[repeat(5,320px)] gap-3 h-full">
          {COLUMNS.map(col => {
            const items = buckets[col.key] || [];
            return (
              <div
                key={col.key}
                className="relative bg-board-lane border border-hair-admin rounded-xl flex flex-col h-full overflow-hidden"
              >
                {/* Top accent strip — column status colour. */}
                <span aria-hidden="true" className="absolute top-0 left-0 right-0 h-0.5" style={{ background: col.accent }} />

                {/* Header */}
                <div className="px-4 py-3 bg-board-lane-head border-b border-hair-admin flex items-center justify-between flex-shrink-0">
                  <div className="flex items-center gap-2">
                    <span
                      className="font-['Metropolis'] text-[11px] font-bold tracking-[2px] uppercase"
                      style={{ color: col.head }}>
                      {col.label}
                    </span>
                    <span className="font-['Metropolis'] text-[10px] font-bold text-[#51534A] bg-count-chip rounded-full px-2 py-0.5">
                      {items.length}
                    </span>
                  </div>
                  {col.key === 'draft' && (
                    <button
                      type="button"
                      title="Create a new brief"
                      onClick={() => navigate('/briefs/new')}
                      className="text-[#51534A] hover:text-active-green text-lg leading-none px-1.5 py-0.5 rounded-md hover:bg-active-green/10 transition-colors">
                      +
                    </button>
                  )}
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-2 flex flex-col gap-2 min-h-0">
                  {items.length === 0 ? (
                    col.key === 'closed' ? (
                      // Sleeping Otto — nothing's wrapped yet (build #10).
                      <div className="flex-1 flex flex-col items-center justify-center text-center px-4 py-6 gap-3">
                        <OttoSleeping size={80} />
                        <p className="text-[#51534A] text-xs italic opacity-70 leading-relaxed max-w-[24ch] break-words">{col.empty}</p>
                      </div>
                    ) : col.key === 'draft' ? (
                      // Draft column gets a light nudge in Otto's voice (no
                      // full art — the column shouldn't shout).
                      <div className="flex-1 flex flex-col items-center justify-center text-center px-3 py-6 gap-2">
                        <p className="text-[#51534A] text-xs italic opacity-70 leading-relaxed">Nothing in draft — start one whenever inspiration strikes.</p>
                        <button
                          type="button"
                          onClick={() => navigate('/briefs/new')}
                          className="font-['Metropolis'] text-[11px] font-bold tracking-[0.12em] uppercase text-fusion-emerald hover:brightness-95 transition">
                          + Create brief
                        </button>
                      </div>
                    ) : (
                      <div className="flex-1 flex items-center justify-center text-center text-[#51534A] text-xs italic opacity-60 px-3 py-6 leading-relaxed">
                        {col.empty}
                      </div>
                    )
                  ) : items.map(b => (
                    <BriefCard
                      key={b.id}
                      brief={b}
                      onClick={() => navigate(`/briefs/${b.id}`)}
                      isResponsesColumn={col.key === 'responses_in'}
                      commentCount={commentCountsByBrief?.get(b.id) || 0} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function BriefCard({ brief, onClick, isResponsesColumn, commentCount = 0 }) {
  const urg = urgencyState(brief);
  const deadline = formatDeadline(brief);
  const owner = (brief.owners_list && brief.owners_list[0]) || null;
  const ownerColor = ownerColorFor(owner?.id || brief.owner_id || '');
  const accentLeftColor = urg === 'overdue' ? '#E0290A' : urg === 'due_soon' ? '#2E8FA1' : null;
  return (
    <div
      onClick={onClick}
      className={`relative bg-white border rounded-md p-3 cursor-pointer transition-all hover:-translate-y-px hover:bg-white ${
        urg === 'overdue' ? 'border-[#E0290A]/40'
          : urg === 'due_soon' ? 'border-[#2E8FA1]/40'
          : 'border-line hover:border-[#DADCCD]'
      }`}
    >
      {accentLeftColor && (
        <span
          aria-hidden="true"
          className="absolute left-0 top-0 bottom-0 w-0.5 rounded-l-md"
          style={{ background: accentLeftColor }} />
      )}
      <div className="flex items-center justify-between mb-2 gap-2">
        <span className="font-['Metropolis'] tabular-nums text-[10px] tracking-wide text-[#51534A] truncate">{brief.fusion_project_number || ''}</span>
        {brief.brief_category && (
          <span className="font-['Metropolis'] text-[9px] font-bold tracking-[2px] uppercase text-[#51534A] border border-line rounded-full px-2 py-0.5 whitespace-nowrap">
            {brief.brief_category}
          </span>
        )}
      </div>
      <p className="font-['Metropolis'] text-[13px] font-bold text-ink leading-snug mb-1">{brief.brief_name}</p>
      {brief.fusion_project_name && (
        <p className="font-['Metropolis'] text-[11px] text-[#51534A] mb-3 leading-snug">{brief.fusion_project_name}</p>
      )}
      {commentCount > 0 && (
        <span
          title={`${commentCount} internal comment${commentCount === 1 ? '' : 's'}`}
          className="inline-flex items-center gap-1 text-[#2E8FA1] font-['Metropolis'] text-[10px] font-bold mb-2">
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
          {commentCount}
        </span>
      )}
      <div className="flex items-center justify-between gap-2 pt-2 border-t border-line">
        <div className={`flex items-center gap-1.5 text-[11px] font-semibold ${
          deadline.tone === 'overdue' ? 'text-[#E0290A]'
            : deadline.tone === 'urgent' ? 'text-[#2E8FA1]'
            : 'text-fusion-mid-grey'
        }`}>
          {deadline.tone === 'overdue' ? (
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
              <line x1="12" y1="9" x2="12" y2="13"/>
              <line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
          ) : deadline.tone === 'urgent' ? (
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <circle cx="12" cy="12" r="10"/>
              <polyline points="12 6 12 12 16 14"/>
            </svg>
          ) : (
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <rect x="3" y="4" width="18" height="18" rx="2"/>
              <line x1="16" y1="2" x2="16" y2="6"/>
              <line x1="8" y1="2" x2="8" y2="6"/>
              <line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
          )}
          {deadline.text}
        </div>
        <div className="flex items-center gap-1.5">
          {isResponsesColumn && (brief.response_count || 0) > 0 && (
            <span className="inline-flex items-center gap-1 font-['Metropolis'] text-[10px] font-bold text-[#2E8FA1] bg-[#2E8FA1]/10 border border-[#2E8FA1]/30 rounded-full px-2 py-0.5">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
              </svg>
              {brief.response_count}
            </span>
          )}
          {owner && (
            <span
              className="w-[22px] h-[22px] rounded-full flex items-center justify-center font-['Metropolis'] text-[10px] font-bold flex-shrink-0"
              style={{ background: ownerColor.bg, color: ownerColor.text }}
              title={owner.full_name}>
              {initialsFor(owner.full_name)}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
